import { useState, useEffect, MutableRefObject } from 'react';
import { Search, Plus, Trash2, Pencil as Edit2, MessageSquare, X } from 'lucide-react';
import { salesCoachService, ChatSession } from '../../services/salesCoachService';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface ChatSidebarProps {
  activeChatThreadId: string | null;
  onSessionSelect: (chatThreadId: string) => void;
  onNewChat: () => void;
  isOpen: boolean;
  onClose: () => void;
  refreshRef?: MutableRefObject<(() => void) | null>;
}

export function ChatSidebar({
  activeChatThreadId,
  onSessionSelect,
  onNewChat,
  isOpen,
  onClose,
  refreshRef
}: ChatSidebarProps) {
  const tz = useTimezone();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (refreshRef) {
      refreshRef.current = () => loadSessions();
    }
    return () => {
      if (refreshRef) {
        refreshRef.current = null;
      }
    };
  }, [refreshRef]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const allSessions = await salesCoachService.getAllSessions();
      setSessions(allSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);

    if (!query.trim()) {
      await loadSessions();
      setIsSearching(false);
      return;
    }

    try {
      setIsSearching(true);
      const results = await salesCoachService.searchSessions(query);
      setSessions(results);
    } catch (error) {
      console.error('Error searching sessions:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleDeleteSession = async (chatThreadId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!confirm('Are you sure you want to delete this chat?')) {
      return;
    }

    try {
      await salesCoachService.deleteSession(chatThreadId);
      await loadSessions();

      if (activeChatThreadId === chatThreadId) {
        onNewChat();
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const startEditing = (session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.chat_thread_id);
    setEditTitle(session.session_title);
  };

  const handleSaveTitle = async (sessionId: string) => {
    if (!editTitle.trim()) return;

    try {
      await salesCoachService.updateSessionTitle(sessionId, editTitle);
      await loadSessions();
      setEditingSessionId(null);
    } catch (error) {
      console.error('Error updating title:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, sessionId: string) => {
    if (e.key === 'Enter') {
      handleSaveTitle(sessionId);
    } else if (e.key === 'Escape') {
      setEditingSessionId(null);
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <div className={`
        fixed lg:sticky top-0 left-0 h-full w-64 bg-gradient-to-br from-white to-[#1875FF]/5
        border-r-2 border-[#1875FF]/20 flex flex-col z-50 transition-transform duration-300
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 border-b-2 border-[#1875FF]/20">
          <div className="flex items-center justify-end mb-4 lg:hidden">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <button
            onClick={onNewChat}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl shadow-sm hover:shadow-md transition-all font-medium"
          >
            <Plus size={14} />
            New Chat
          </button>
        </div>

        <div className="p-4 border-b-2 border-[#1875FF]/20">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border-2 border-gray-200 rounded-lg
                       text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#1875FF]
                       focus:ring-2 focus:ring-[#1875FF] transition-all shadow-sm font-medium"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-white to-[#1875FF]/5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-700 font-medium">Loading chats...</div>
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <MessageSquare className="text-[#1875FF]/30 mb-2" size={32} />
              <p className="text-gray-700 text-sm font-bold">
                {searchQuery ? 'No chats found' : 'No chats yet'}
              </p>
              <p className="text-gray-600 text-xs mt-1 font-medium">
                {searchQuery ? 'Try a different search' : 'Start a new conversation'}
              </p>
            </div>
          ) : (
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                Recent Chats
              </div>
              {sessions.map((session) => (
                <div
                  key={session.chat_thread_id}
                  onClick={() => {
                    onSessionSelect(session.chat_thread_id);
                    onClose();
                  }}
                  className={`
                    mx-2 mb-1 px-3 py-3 rounded-lg cursor-pointer transition-all duration-200
                    group relative
                    ${activeChatThreadId === session.chat_thread_id
                      ? 'bg-gradient-to-br from-[#1875FF]/10 to-[#1875FF]/5 text-[#1875FF] border-2 border-[#1875FF]/30 shadow-md'
                      : 'hover:bg-white/70 text-gray-700 border-2 border-transparent hover:border-[#1875FF]/20'
                    }
                  `}
                >
                  {editingSessionId === session.chat_thread_id ? (
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onBlur={() => handleSaveTitle(session.chat_thread_id)}
                      onKeyDown={(e) => handleKeyPress(e, session.chat_thread_id)}
                      className="w-full bg-white text-gray-900 px-2 py-1 rounded border-2 border-gray-300
                               focus:outline-none focus:border-[#1875FF] text-sm font-medium"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">
                          {session.session_title || 'New Chat'}
                        </div>
                        <div className={`text-xs mt-1 font-medium ${
                          activeChatThreadId === session.chat_thread_id
                            ? 'text-[#1875FF]'
                            : 'text-gray-600'
                        }`}>
                          {formatInTz(new Date(session.session_updated_at), 'MMM d, h:mm a', tz)}
                        </div>
                      </div>

                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => startEditing(session, e)}
                          className={`p-1 rounded hover:bg-[#1875FF]/10 transition-colors ${
                            activeChatThreadId === session.chat_thread_id
                              ? 'text-[#1875FF]'
                              : 'text-gray-600'
                          }`}
                          title="Rename chat"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={(e) => handleDeleteSession(session.chat_thread_id, e)}
                          className="p-1 rounded hover:bg-red-100 text-gray-600 hover:text-red-600 transition-colors"
                          title="Delete chat"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
